import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  artLabel, countAttendance, weekdayLabelOfIso, formatDateDE, WEEKDAYS,
} from "@/lib/training";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DuplicateSessionDialog from "@/components/DuplicateSessionDialog";
import FormEditorDialog from "@/components/FormEditorDialog";
import { FORM_TYPES, FORM_TYPE_LABEL, FORM_TYPE_ACCENT } from "@/lib/formTypes";
import {
  Plus, MapPin, Clock, ChevronLeft, ChevronRight, CalendarDays, List,
  CheckCircle2, AlertCircle, XCircle, BarChart3, Activity, ClipboardList,
  Library, Filter, Copy, ChevronDown, ChevronUp, X,
  Search, Pencil, Trash2, Users,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function monthLabel(d) {
  return d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}
function addDaysIso(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return isoDate(d);
}

const TABS = [
  { key: "library", label: "Bibliothek", icon: Library },
  { key: "list", label: "Liste", icon: List },
  { key: "calendar", label: "Kalender", icon: CalendarDays },
];

export default function Training() {
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [perfTests, setPerfTests] = useState([]);
  const [sessions, setSessions] = useState([]);   // Alle Trainingseinheiten
  const [forms, setForms] = useState([]);          // Übungs- & Spielformen
  const [focusAreas, setFocusAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");       // Default = Liste
  const [libType, setLibType] = useState("sessions"); // Bibliothek-Untertab (bleibt über Reloads erhalten)
  const [monthCursor, setMonthCursor] = useState(startOfMonth(new Date()));

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, pRes, sRes, faRes, fRes] = await Promise.all([
        api.get("/trainings"),
        api.get("/performance-tests"),
        api.get("/training-sessions"),
        api.get("/focus-areas"),
        api.get("/exercise-forms"),
      ]);
      setTrainings(tRes.data);
      setPerfTests(pRes.data);
      setSessions(sRes.data);
      setFocusAreas(faRes.data);
      setForms(fRes.data);
    } catch {
      toast.error("Trainings konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Stiller Reload nur der Formen (ohne globalen Spinner) — hält Filter/Suche im Forms-Tab.
  const reloadForms = async () => {
    try {
      const { data } = await api.get("/exercise-forms");
      setForms(data);
    } catch {
      // ignorieren
    }
  };

  // Session-Map: training_id -> session summary
  const sessionByTraining = useMemo(() => {
    const m = new Map();
    for (const s of sessions) m.set(s.training_id, s);
    return m;
  }, [sessions]);

  // Merge trainings + performance tests as unified events (used by Liste & Kalender).
  const sorted = useMemo(() => {
    const trainingEvents = trainings.map((t) => ({
      ...t, kind: "training", uhrzeitForSort: t.uhrzeit,
    }));
    const perfEvents = perfTests.map((p) => ({
      id: p.id,
      datum: p.datum,
      uhrzeit: p.uhrzeit_start || "",
      end_zeit: p.uhrzeit_ende || "",
      ort: "",
      art: "leistungstest",
      art_label: p.test_name,
      dimension: p.dimension,
      kind: "perf",
      uhrzeitForSort: p.uhrzeit_start || "00:00",
    }));
    return [...trainingEvents, ...perfEvents].sort(
      (a, b) => (a.datum + (a.uhrzeitForSort || "")).localeCompare(b.datum + (b.uhrzeitForSort || ""))
    );
  }, [trainings, perfTests]);

  const byDate = useMemo(() => {
    const m = new Map();
    for (const t of sorted) {
      if (!m.has(t.datum)) m.set(t.datum, []);
      m.get(t.datum).push(t);
    }
    return m;
  }, [sorted]);

  const todayIso = isoDate(new Date());

  const goEvent = (ev) => {
    if (ev.kind === "perf") navigate(`/leistungstest/${ev.id}`);
    else navigate(`/training/${ev.id}`);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-1">Modul</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-100">Training</h1>
          <p className="text-zinc-400 text-sm mt-1">
            <span className="text-zinc-100 font-semibold" data-testid="trainings-count">{trainings.length}</span> Trainings
            {perfTests.length > 0 && <> · <span className="text-zinc-100 font-semibold" data-testid="perftests-count">{perfTests.length}</span> Leistungstests</>}
            {sessions.length > 0 && <> · <span className="text-zinc-100 font-semibold" data-testid="sessions-count">{sessions.length}</span> Einheiten</>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => navigate("/training/saison")}
            variant="outline"
            className="bg-transparent border-zinc-700 text-zinc-100 hover:text-brand-lighter hover:bg-zinc-900 rounded-md h-11"
            data-testid="season-btn"
          >
            <BarChart3 className="h-4 w-4 mr-2" /> Saisonübersicht
          </Button>
          <Button
            onClick={() => navigate("/training/neu")}
            className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11"
            data-testid="new-training-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Neues Training
          </Button>
        </div>
      </div>

      {/* Submenü: Bibliothek | Liste | Kalender */}
      <div className="grid grid-cols-3 gap-1 p-1 rounded-md bg-zinc-950 border border-zinc-800 mb-5" data-testid="training-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = view === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setView(tab.key)}
              className={
                "min-h-[40px] inline-flex items-center justify-center gap-2 rounded text-xs sm:text-sm font-medium tracking-wide transition " +
                (active
                  ? "bg-brand text-white"
                  : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900")
              }
              data-testid={`view-${tab.key}`}
            >
              <Icon className="h-4 w-4" /> {tab.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-500">Lädt…</div>
      ) : view === "library" ? (
        <LibraryView
          sessions={sessions}
          forms={forms}
          focusAreas={focusAreas}
          trainings={trainings}
          todayIso={todayIso}
          libType={libType}
          setLibType={setLibType}
          onOpen={(s) => navigate(`/training/${s.training_id}/einheit/ansehen`)}
          onDuplicated={load}
          onReload={reloadForms}
        />
      ) : view === "list" ? (
        sorted.length === 0 ? (
          <EmptyState onCreate={() => navigate("/training/neu")} />
        ) : (
          <ListView
            events={sorted}
            todayIso={todayIso}
            sessionByTraining={sessionByTraining}
            onOpen={goEvent}
          />
        )
      ) : (
        <CalendarView
          monthCursor={monthCursor}
          setMonthCursor={setMonthCursor}
          byDate={byDate}
          todayIso={todayIso}
          onOpen={(ev) => goEvent(ev)}
          sessionByTraining={sessionByTraining}
        />
      )}
    </div>
  );
}

// -------------------- Liste (jetzt Standard) --------------------

function ListView({ events, todayIso, sessionByTraining, onOpen }) {
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showAllPast, setShowAllPast] = useState(false);

  const upperIso = addDaysIso(todayIso, 28);
  const lowerIso = addDaysIso(todayIso, -28);

  const upcoming = events.filter((e) => e.datum >= todayIso);
  const past = events.filter((e) => e.datum < todayIso).slice().reverse();

  const upcomingWindow = upcoming.filter((e) => e.datum <= upperIso);
  const pastWindow = past.filter((e) => e.datum >= lowerIso);

  const upcomingShown = showAllUpcoming ? upcoming : upcomingWindow;
  const pastShown = showAllPast ? past : pastWindow;

  const next = upcoming[0] || null;
  const rest = upcomingShown.filter((e) => !next || `${e.kind}-${e.id}` !== `${next.kind}-${next.id}`);

  return (
    <div className="space-y-6" data-testid="trainings-list">
      {next && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-widest text-brand-lighter font-semibold">Nächstes Training</span>
            <div className="flex-1 h-px bg-brand/30" />
          </div>
          <TrainingRow
            t={next}
            onOpen={() => onOpen(next)}
            todayIso={todayIso}
            session={next.kind === "training" ? sessionByTraining.get(next.id) : null}
            highlight
          />
        </div>
      )}

      {rest.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Kommende Trainings</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>
          <div className="space-y-2">
            {rest.map((t) => (
              <TrainingRow
                key={`${t.kind}-${t.id}`}
                t={t}
                onOpen={() => onOpen(t)}
                todayIso={todayIso}
                session={t.kind === "training" ? sessionByTraining.get(t.id) : null}
              />
            ))}
          </div>
          {!showAllUpcoming && upcoming.length > upcomingWindow.length && (
            <button
              onClick={() => setShowAllUpcoming(true)}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs text-zinc-400 hover:text-brand-lighter transition py-3 rounded-md border border-dashed border-zinc-800 hover:border-brand-lighter/40"
              data-testid="expand-upcoming"
            >
              <ChevronDown className="h-3.5 w-3.5" /> Weitere {upcoming.length - upcomingWindow.length} kommende anzeigen
            </button>
          )}
          {showAllUpcoming && upcoming.length > upcomingWindow.length && (
            <button
              onClick={() => setShowAllUpcoming(false)}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition py-2"
              data-testid="collapse-upcoming"
            >
              <ChevronUp className="h-3.5 w-3.5" /> Zeitraum wieder auf 4 Wochen begrenzen
            </button>
          )}
        </div>
      )}

      {pastShown.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Vergangene Trainings</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>
          <div className="space-y-2">
            {pastShown.map((t) => (
              <TrainingRow
                key={`${t.kind}-${t.id}`}
                t={t}
                onOpen={() => onOpen(t)}
                todayIso={todayIso}
                session={t.kind === "training" ? sessionByTraining.get(t.id) : null}
              />
            ))}
          </div>
          {!showAllPast && past.length > pastWindow.length && (
            <button
              onClick={() => setShowAllPast(true)}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs text-zinc-400 hover:text-brand-lighter transition py-3 rounded-md border border-dashed border-zinc-800 hover:border-brand-lighter/40"
              data-testid="expand-past"
            >
              <ChevronDown className="h-3.5 w-3.5" /> Weitere {past.length - pastWindow.length} vergangene anzeigen
            </button>
          )}
          {showAllPast && past.length > pastWindow.length && (
            <button
              onClick={() => setShowAllPast(false)}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition py-2"
              data-testid="collapse-past"
            >
              <ChevronUp className="h-3.5 w-3.5" /> Zeitraum wieder auf 4 Wochen begrenzen
            </button>
          )}
        </div>
      )}

      {!next && upcoming.length === 0 && pastShown.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/20 p-6 text-center text-sm text-zinc-500">
          Kein Training in den nächsten oder letzten 4 Wochen.
        </div>
      )}
    </div>
  );
}

function TrainingRow({ t, onOpen, todayIso, session, highlight }) {
  const isPerf = t.kind === "perf";
  const c = isPerf ? null : countAttendance(t);
  const isPast = t.datum < todayIso;
  const accent = isPerf
    ? "bg-fuchsia-500/15 text-fuchsia-300"
    : t.art === "bonustraining"
      ? "bg-amber-500/15 text-amber-300"
      : "bg-brand/15 text-brand-lighter";

  const hasSession = !!session;

  return (
    <button
      onClick={onOpen}
      className={
        "w-full text-left rounded-lg border transition p-4 flex flex-col sm:flex-row sm:items-stretch gap-3 " +
        (highlight
          ? "border-brand bg-brand/5 hover:bg-brand/10"
          : "border-zinc-800 bg-zinc-900/40 hover:border-brand-lighter/50 hover:bg-zinc-900") +
        (hasSession ? " border-l-4 border-l-emerald-500" : "")
      }
      data-testid={isPerf ? `perf-row-${t.id}` : `training-row-${t.id}`}
    >
      <div className="flex items-center gap-3 sm:min-w-[180px]">
        <div className={"h-12 w-12 rounded-md flex flex-col items-center justify-center font-display shrink-0 " + accent}>
          {isPerf ? <Activity className="h-4 w-4" /> : <span className="text-[10px] uppercase tracking-wide">{weekdayLabelOfIso(t.datum)}</span>}
          <span className="text-base font-bold leading-none">{t.datum.slice(8, 10)}.{t.datum.slice(5, 7)}</span>
        </div>
        <div className="min-w-0">
          <div className="text-zinc-100 font-medium flex items-center gap-1.5">
            {isPerf ? t.art_label : formatDateDE(t.datum)}
            {hasSession && (
              <span
                className="inline-block h-2 w-2 rounded-full bg-emerald-500 shrink-0"
                title="Trainingseinheit hinterlegt"
                data-testid={`row-session-dot-${t.id}`}
              />
            )}
          </div>
          <div className="text-xs text-zinc-400 flex items-center gap-3 flex-wrap">
            {t.uhrzeit && (
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {t.uhrzeit}{t.end_zeit ? ` – ${t.end_zeit}` : ""}</span>
            )}
            {!isPerf && t.ort && (
              <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {t.ort}</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col gap-2 min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={"inline-flex items-center px-2 py-0.5 rounded font-medium " + (
            isPerf
              ? "text-fuchsia-300 bg-fuchsia-500/10"
              : t.art === "bonustraining"
                ? "text-amber-300 bg-amber-500/10"
                : "text-brand-lighter bg-brand/10"
          )}>
            {isPerf ? "Leistungstest" : artLabel(t.art)}
          </span>
          {isPast && !isPerf && c && (
            <>
              <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-3 w-3" /> {c.anwesend}</span>
              <span className="inline-flex items-center gap-1 text-amber-400"><AlertCircle className="h-3 w-3" /> {c.entschuldigt}</span>
              <span className="inline-flex items-center gap-1 text-rose-400"><XCircle className="h-3 w-3" /> {c.unentschuldigt}</span>
            </>
          )}
        </div>

        {/* Inline Trainingseinheit */}
        {hasSession && (
          <div className="rounded-md border border-emerald-500/25 bg-emerald-500/5 px-2.5 py-2" data-testid={`row-session-${t.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <ClipboardList className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
              <span className="text-xs font-medium text-zinc-100 truncate">
                {session.name || "Trainingsplan"}
              </span>
            </div>
            {(session.focus_names || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {session.focus_names.slice(0, 4).map((n) => (
                  <span key={n} className="px-1.5 py-0.5 rounded-sm text-[10px] bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                    {n}
                  </span>
                ))}
                {session.focus_names.length > 4 && (
                  <span className="text-[10px] text-zinc-500 px-1">+{session.focus_names.length - 4}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

// -------------------- Kalender --------------------

function CalendarView({ monthCursor, setMonthCursor, byDate, todayIso, onOpen, sessionByTraining }) {
  const first = monthCursor;
  const firstWeekday = (first.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(first.getFullYear(), first.getMonth(), day);
    cells.push({ day, iso: isoDate(d) });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 sm:p-5" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setMonthCursor(addMonths(monthCursor, -1))}
          className="h-9 w-9 rounded-md border border-zinc-800 text-zinc-300 hover:text-brand-lighter hover:border-brand-lighter/40 flex items-center justify-center"
          data-testid="cal-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="font-display text-lg sm:text-xl font-semibold capitalize text-zinc-100" data-testid="cal-month">
          {monthLabel(monthCursor)}
        </div>
        <button
          onClick={() => setMonthCursor(addMonths(monthCursor, 1))}
          className="h-9 w-9 rounded-md border border-zinc-800 text-zinc-300 hover:text-brand-lighter hover:border-brand-lighter/40 flex items-center justify-center"
          data-testid="cal-next"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAYS.map((w) => (
          <div key={w.idx} className="text-[11px] uppercase tracking-wide text-zinc-500 text-center">{w.short}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="h-16 sm:h-24" />;
          const list = byDate.get(c.iso) || [];
          const today = c.iso === todayIso;
          return (
            <div
              key={i}
              className={
                "h-16 sm:h-24 rounded-md border p-1 sm:p-1.5 flex flex-col gap-1 overflow-hidden " +
                (today ? "border-brand bg-brand/5" : "border-zinc-800 bg-zinc-950")
              }
            >
              <div className={"text-[11px] font-medium " + (today ? "text-brand-lighter" : "text-zinc-400")}>{c.day}</div>
              <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                {list.slice(0, 2).map((t) => {
                  const isPerf = t.kind === "perf";
                  const hasSession = !isPerf && sessionByTraining.get(t.id);
                  return (
                    <button
                      key={`${t.kind}-${t.id}`}
                      onClick={() => onOpen(t)}
                      className={
                        "text-[10px] sm:text-xs px-1.5 py-0.5 rounded text-left truncate font-medium flex items-center gap-1 " +
                        (isPerf
                          ? "bg-fuchsia-500/15 text-fuchsia-300 hover:bg-fuchsia-500/25"
                          : t.art === "bonustraining"
                            ? "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"
                            : "bg-brand/20 text-brand-lighter hover:bg-brand/35")
                      }
                      title={isPerf ? `${t.uhrzeit || ""} · ${t.art_label}` : `${t.uhrzeit} · ${t.ort}`}
                      data-testid={isPerf ? `cal-perf-${t.id}` : `cal-train-${t.id}`}
                    >
                      {hasSession && <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" />}
                      <span className="truncate">
                        {isPerf
                          ? <>{t.uhrzeit ? `${t.uhrzeit} ` : ""}·LT</>
                          : <>{t.uhrzeit} {t.art === "bonustraining" ? "·B" : ""}</>}
                      </span>
                    </button>
                  );
                })}
                {list.length > 2 && <div className="text-[10px] text-zinc-500">+{list.length - 2}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -------------------- Bibliothek --------------------

const LIB_TABS = [
  { key: "sessions", label: "Trainingseinheiten" },
  { key: "forms", label: "Übungs- & Spielformen" },
];

function LibraryView({ sessions, forms, focusAreas, trainings, todayIso, libType, setLibType, onOpen, onDuplicated, onReload }) {
  return (
    <div className="space-y-4" data-testid="library-view">
      <div className="inline-flex gap-1 p-1 rounded-md bg-zinc-950 border border-zinc-800" data-testid="library-type-switch">
        {LIB_TABS.map((t) => {
          const active = libType === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setLibType(t.key)}
              className={
                "min-h-[36px] px-4 rounded text-xs sm:text-sm font-medium tracking-wide transition " +
                (active ? "bg-brand text-white" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900")
              }
              data-testid={`library-type-${t.key}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {libType === "sessions" ? (
        <SessionsLibrary
          sessions={sessions}
          focusAreas={focusAreas}
          trainings={trainings}
          todayIso={todayIso}
          onOpen={onOpen}
          onDuplicated={onDuplicated}
        />
      ) : (
        <FormsLibrary forms={forms} focusAreas={focusAreas} onReload={onReload} />
      )}
    </div>
  );
}

function SessionsLibrary({ sessions, focusAreas, trainings, todayIso, onOpen, onDuplicated }) {
  const [selectedFas, setSelectedFas] = useState([]);
  const [templateSession, setTemplateSession] = useState(null); // Session, aus der dupliziert werden soll

  // Bibliothek zeigt nur "eigenständige" Einheiten (keine ungeänderten Kopien).
  const librarySessions = useMemo(
    () => sessions.filter((s) => !s.is_duplicate),
    [sessions],
  );

  // FocusAreas, die tatsächlich in Bibliotheks-Sessions genutzt werden – für den Filter
  const usedFas = useMemo(() => {
    const usedIds = new Set();
    for (const s of librarySessions) for (const id of s.focus_area_ids || []) usedIds.add(id);
    return focusAreas.filter((fa) => usedIds.has(fa.id));
  }, [librarySessions, focusAreas]);

  const filtered = useMemo(() => {
    if (selectedFas.length === 0) return librarySessions;
    const set = new Set(selectedFas);
    return librarySessions.filter((s) => (s.focus_area_ids || []).some((id) => set.has(id)));
  }, [librarySessions, selectedFas]);

  const toggleFa = (id) => {
    setSelectedFas((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  if (librarySessions.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/20 p-10 text-center" data-testid="library-empty">
        <Library className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
        <div className="font-display text-xl font-semibold text-zinc-200 mb-1">Noch keine Trainingseinheiten in der Bibliothek</div>
        <div className="text-sm text-zinc-500">
          Öffne einen Trainingstermin und lege dort deine erste Einheit an. Duplikate erscheinen erst hier, sobald du sie umbenennst.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {usedFas.length > 0 && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="h-3.5 w-3.5 text-zinc-400" />
            <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">Nach Schwerpunkt filtern</span>
            {selectedFas.length > 0 && (
              <button
                onClick={() => setSelectedFas([])}
                className="ml-auto text-xs text-zinc-400 hover:text-brand-lighter transition inline-flex items-center gap-1"
                data-testid="library-clear-filter"
              >
                <X className="h-3 w-3" /> Zurücksetzen
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {usedFas.map((fa) => {
              const active = selectedFas.includes(fa.id);
              return (
                <button
                  key={fa.id}
                  onClick={() => toggleFa(fa.id)}
                  className={
                    "min-h-[32px] px-3 rounded-full text-xs font-medium tracking-wide transition border " +
                    (active
                      ? "bg-brand text-white border-brand"
                      : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-brand-lighter/40 hover:text-brand-lighter")
                  }
                  data-testid={`library-filter-${fa.id}`}
                >
                  {fa.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/20 p-8 text-center text-sm text-zinc-500">
          Keine Trainingseinheiten mit diesem Filter.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="library-grid">
          {filtered.map((s) => (
            <LibraryCard
              key={s.id}
              session={s}
              todayIso={todayIso}
              onOpen={() => onOpen(s)}
              onTemplate={(e) => { e.stopPropagation(); setTemplateSession(s); }}
            />
          ))}
        </div>
      )}

      <DuplicateSessionDialog
        source={templateSession}
        trainings={trainings}
        sessions={sessions}
        todayIso={todayIso}
        onClose={() => setTemplateSession(null)}
        onSuccess={() => { setTemplateSession(null); onDuplicated?.(); }}
      />
    </div>
  );
}

// -------------------- Übungs- & Spielformen --------------------

function FormsLibrary({ forms, focusAreas, onReload }) {
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [focusFilter, setFocusFilter] = useState([]);
  const [editor, setEditor] = useState(null);   // { editId?, initial? } | null
  const [deleteForm, setDeleteForm] = useState(null);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return forms.filter((f) => {
      if (typeFilter && f.form_type !== typeFilter) return false;
      if (focusFilter.length && !focusFilter.every((id) => (f.focus_area_ids || []).includes(id))) return false;
      if (term) {
        const hay = `${f.name} ${(f.focus_names || []).join(" ")} ${f.ablauf || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [forms, q, typeFilter, focusFilter]);

  const toggleFocus = (id) =>
    setFocusFilter((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const resetFilters = () => { setQ(""); setTypeFilter(""); setFocusFilter([]); };

  const duplicateForm = (f) => {
    // Kopie als neuer Eintrag: alle Inhalte übernehmen, Name vorschlagen, keine editId → POST.
    setEditor({ initial: { ...f, id: undefined, name: `Kopie von ${f.name}` } });
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/exercise-forms/${deleteForm.id}`);
      toast.success("Form gelöscht");
      setDeleteForm(null);
      onReload?.();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Suche nach Name, Schwerpunkt…"
            className="bg-zinc-900 border-zinc-800 text-zinc-100 h-11 pl-9 focus-visible:ring-brand"
            data-testid="forms-search" />
        </div>
        <Button onClick={() => setEditor({})} className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11" data-testid="forms-new-btn">
          <Plus className="h-4 w-4 mr-2" /> Neue Form
        </Button>
      </div>

      {(forms.length > 0) && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 space-y-3">
          <div data-testid="forms-type-group">
            <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">Form-Typ</div>
            <div className="flex flex-wrap gap-1.5" data-testid="forms-type-filters">
              <FormFilterChip active={typeFilter === ""} onClick={() => setTypeFilter("")} label="Alle Typen" testId="forms-type-all" />
              {FORM_TYPES.map((t) => (
                <FormFilterChip key={t.key} active={typeFilter === t.key} onClick={() => setTypeFilter(t.key)} label={t.label} testId={`forms-type-${t.key}`} />
              ))}
            </div>
          </div>
          {focusAreas.length > 0 && (
            <div data-testid="forms-focus-group">
              <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">Trainingsschwerpunkte</div>
              <div className="flex flex-wrap gap-1.5" data-testid="forms-focus-filters">
                <FormFilterChip active={focusFilter.length === 0} onClick={() => setFocusFilter([])} label="Alle Schwerpunkte" testId="forms-focus-all" />
                {focusAreas.map((fa) => (
                  <FormFilterChip key={fa.id} active={focusFilter.includes(fa.id)} onClick={() => toggleFocus(fa.id)} label={fa.name} subtle testId={`forms-focus-${fa.id}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {forms.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/20 p-10 text-center" data-testid="forms-empty">
          <Library className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <div className="font-display text-xl font-semibold text-zinc-200 mb-1">Noch keine Übungs- & Spielformen</div>
          <div className="text-sm text-zinc-500 mb-5">
            Lege wiederverwendbare Bausteine an – oder speichere sie direkt aus einer Trainingseinheit.
          </div>
          <Button onClick={() => setEditor({})} className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md">
            <Plus className="h-4 w-4 mr-2" /> Erste Form anlegen
          </Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/20 p-8 text-center" data-testid="forms-no-results">
          <div className="text-sm text-zinc-400 mb-3">Keine Formen passen zu deiner Suche und den aktiven Filtern.</div>
          <Button variant="outline" onClick={resetFilters}
            className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900 rounded-md" data-testid="forms-reset-filters">
            <X className="h-4 w-4 mr-2" /> Filter zurücksetzen
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="forms-grid">
          {filtered.map((f) => (
            <FormCard key={f.id} form={f}
              onEdit={() => setEditor({ editId: f.id, initial: f })}
              onDuplicate={() => duplicateForm(f)}
              onDelete={() => setDeleteForm(f)} />
          ))}
        </div>
      )}

      <FormEditorDialog
        open={!!editor}
        editId={editor?.editId}
        initial={editor?.initial}
        focusAreas={focusAreas}
        onClose={() => setEditor(null)}
        onSaved={() => { setEditor(null); onReload?.(); }}
      />

      <AlertDialog open={!!deleteForm} onOpenChange={(o) => !o && setDeleteForm(null)}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg font-semibold tracking-tight">Form löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              „{deleteForm?.name}" wird aus der Bibliothek entfernt. Bereits in Trainingseinheiten übernommene Kopien bleiben unverändert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="forms-delete-confirm">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FormFilterChip({ active, onClick, label, subtle, testId }) {
  return (
    <button type="button" onClick={onClick}
      className={
        "min-h-[32px] px-3 rounded-full text-xs font-medium tracking-wide transition border " +
        (active
          ? "bg-brand text-white border-brand"
          : subtle
            ? "bg-zinc-950 text-zinc-400 border-zinc-800 hover:text-zinc-100 hover:border-zinc-700"
            : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-brand-lighter/40 hover:text-brand-lighter")
      }
      data-testid={testId}>
      {label}
    </button>
  );
}

function FormCard({ form, onEdit, onDuplicate, onDelete }) {
  const spieler = form.spieler_von || form.spieler_bis
    ? `${form.spieler_von ?? "?"}${form.spieler_bis ? `–${form.spieler_bis}` : ""}`
    : null;
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 hover:border-brand-lighter/50 transition p-4 flex flex-col gap-3" data-testid={`form-card-${form.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <span className={"inline-flex items-center px-2 py-0.5 rounded text-[9px] uppercase tracking-wide font-bold border mb-1.5 " + (FORM_TYPE_ACCENT[form.form_type] || "")}>
            {FORM_TYPE_LABEL[form.form_type] || form.form_type}
          </span>
          <div className="font-display text-base font-bold text-zinc-100 leading-tight break-words">{form.name}</div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onEdit} aria-label="Form bearbeiten" title="Bearbeiten" data-testid={`form-card-edit-${form.id}`}
            className="h-9 w-9 rounded-md border border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-brand-lighter hover:border-brand-lighter/40 flex items-center justify-center transition">
            <Pencil className="h-4 w-4" />
          </button>
          <button onClick={onDuplicate} aria-label="Form duplizieren" title="Form duplizieren" data-testid={`form-card-duplicate-${form.id}`}
            className="h-9 w-9 rounded-md border border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-brand-lighter hover:border-brand-lighter/40 flex items-center justify-center transition">
            <Copy className="h-4 w-4" />
          </button>
          <button onClick={onDelete} aria-label="Form löschen" title="Löschen" data-testid={`form-card-delete-${form.id}`}
            className="h-9 w-9 rounded-md border border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-rose-400 hover:border-rose-500/40 flex items-center justify-center transition">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {(form.focus_names || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {form.focus_names.map((n) => (
            <span key={n} className="px-2 py-0.5 rounded-full text-[10px] bg-brand/15 border border-brand/30 text-brand-lighter">{n}</span>
          ))}
        </div>
      )}

      {form.ablauf && (
        <p className="text-xs text-zinc-400 line-clamp-2">{form.ablauf}</p>
      )}

      <div className="mt-auto flex items-center gap-3 text-[11px] text-zinc-500 border-t border-zinc-800 pt-2">
        {form.dauer_min ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {form.dauer_min} Min.</span> : null}
        {spieler ? <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {spieler} Spieler</span> : null}
        {!form.dauer_min && !spieler && <span>—</span>}
      </div>
    </div>
  );
}


function LibraryCard({ session, todayIso, onOpen, onTemplate }) {
  const dateLabel = session.training_datum ? formatDateDE(session.training_datum) : "—";
  const isPast = session.training_datum && session.training_datum < todayIso;
  return (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900/40 hover:border-brand-lighter/50 hover:bg-zinc-900 transition p-4 flex flex-col gap-3 cursor-pointer group"
      onClick={onOpen}
      data-testid={`library-card-${session.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-brand-lighter mb-0.5">
            {dateLabel}{session.training_uhrzeit ? ` · ${session.training_uhrzeit}` : ""}
          </div>
          <div className="font-display text-base sm:text-lg font-bold text-zinc-100 leading-tight break-words">
            {session.name || "Ohne Bezeichnung"}
          </div>
        </div>
        <button
          onClick={onTemplate}
          className="shrink-0 h-9 w-9 rounded-md border border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-brand-lighter hover:border-brand-lighter/40 flex items-center justify-center transition"
          title="Als Vorlage nutzen"
          data-testid={`library-template-${session.id}`}
        >
          <Copy className="h-4 w-4" />
        </button>
      </div>

      {(session.focus_names || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {session.focus_names.map((n) => (
            <span key={n} className="px-2 py-0.5 rounded-full text-[10px] bg-brand/15 border border-brand/30 text-brand-lighter">
              {n}
            </span>
          ))}
        </div>
      )}

      {(session.block_summary || []).length > 0 && (
        <div className="mt-1 space-y-1 text-xs text-zinc-400 border-t border-zinc-800 pt-2">
          {session.block_summary.slice(0, 3).map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="font-display text-[10px] font-bold text-zinc-600 tabular-nums shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="truncate">{b.uebung}</span>
              {b.dauer_min && <span className="text-zinc-600 shrink-0">· {b.dauer_min} min</span>}
            </div>
          ))}
        </div>
      )}

      <div className="mt-auto text-[10px] uppercase tracking-wider text-zinc-600">
        {isPast ? "Aus Vergangenheit" : "Geplant"}
      </div>
    </div>
  );
}

// -------------------- Empty --------------------

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-10 text-center">
      <CalendarDays className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
      <div className="font-display text-xl font-semibold text-zinc-200 mb-1">Noch keine Trainings</div>
      <div className="text-sm text-zinc-500 mb-5">Lege deinen ersten Trainingstermin an.</div>
      <Button onClick={onCreate} className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md">
        <Plus className="h-4 w-4 mr-2" /> Neues Training
      </Button>
    </div>
  );
}
